<template>
	<div>
		<loader v-if="loading"></loader>
		<div v-else class="space-y-8 mb-8">
			<div class="card card-white mx-auto max-w-4xl p-0 rounded-xl">
				<user-cover
					v-if="user.is_active"
					:canupload="isCurrentUserProfile && editMode"
					:userid="user.id"
					:cursor="user.cursor"
					:url="user.cov"
					class="bg-biege overflow-hidden rounded-t-xl"
				></user-cover>

				<div class="p-6">
					<div class="flex flex-col-reverse md:flex-row md:justify-between md:items-start md:space-x-6">
						<div class="flex items-start space-x-4 flex-1">
							<div class="w-16">
								<div v-if="!user.is_active"
									 class="rounded-full w-16 h-16 bg-gray-100 dark:bg-slate-600 flex items-center justify-center text-gray-300">
									<svg-icon icon="skull" set="local" class="w-12 h-12"></svg-icon>
								</div>
								<div v-else class="w-16 h-16">
									<ava v-if="isCurrentUserProfile && editMode" :canupload="1" :url="user.ava"
										 class="w-16 h-16 ring ring-blue-500/50"></ava>
									<avatar v-else :ava="user.ava" :online="user.is_online" lightbox="1"
											class="w-16 h-16"></avatar>
								</div>
							</div>
							<div class="flex-1">
								<div
									class="text-xl md:text-2xl space-x-2 lg:text-4xl font-medium flex items-center leading-none">
									<div>{{ user.name }}</div>
									<svg-icon v-if="user.is_bot" icon="robot" set="local"
											  class="w-5 h-5 text-orange shrink-0"></svg-icon>
									<svg-icon v-if="user.is_verified" icon="check-circle-fill"
											  class="w-5 h-5 text-blue-500 shrink-0"></svg-icon>
								</div>

								<div v-if="user.position" class="opacity-50 mb-2">
									{{ user.position }}
								</div>

								<div
									class="flex flex-col md:flex-row my-2 md:items-center md:space-x-4 text-sm text-gray-500">
									<rating :rate="user.rating" :add-plus="1"></rating>

									<a @click.prevent="setPartByValue('subscribers', true)"
									   :href="user.url + '/subscribers'" class="text-inherit">
										<div v-if="user.subscribers_count">
											{{ plural(user.subscribers_count) }}
										</div>
										<div v-else>
											Нет подписчиков
										</div>
									</a>

									<div>На сайте с {{ user.registered }}</div>
								</div>
							</div>
						</div>
						<div class="flex items-center justify-end md:justify-start space-x-2 mb-4">
							<a v-if="isCurrentUserProfile" href="/u/settings" class="btn btn-md btn-default">
								<svg-icon icon="gear" class="w-4 h-4"></svg-icon>
							</a>
							<div
								v-if="isCurrentUserProfile"
								@click="editMode = !editMode" class="btn btn-md cursor-pointer"
								:class="{'btn-default': !editMode, 'btn-success': editMode}"
								title="Редактировать">
								<svg-icon v-if="!editMode" icon="pencil" class="w-4 h-4"></svg-icon>
								<svg-icon v-else icon="check" class="w-4 h-4"></svg-icon>
							</div>
						</div>
					</div>

					<div v-if="user.is_active && !user.ban && user.description"
						 class="break-words mt-2 w-full max-w-full text-sm text-gray-500"
						 v-html="user.description">
					</div>

					<div v-if="user.ban" class="my-4">
                        <span class="bg-red-500 text-white rounded-xl leading-none text-xs py-1 px-2">
                            Бан
                            <span v-if="user.ban.permanent">
                                пермач
                            </span>
                            <span v-else>
                                до <smartdate
								class="inline"
								:date="user.ban.banned_until"
								:direct="1"
								:ignore-future="1"
							></smartdate>
                            </span>
                        </span>
					</div>

					<div v-if="!isCurrentUserProfile && isValentineDaySendPeriod" class="my-4">
						<valentine-form :userid="user.id" :sent="user.valentineIsSent"></valentine-form>
					</div>

					<div class="rounded bg-slate-50 dark:bg-slate-700 my-4 p-3 text-sm">
						<user-stat-row
							class="mb-1 text-xs opacity-40"
							label="Статистика"
							value="Кол-во"
							total-rate="Сум.оцен"
							rate="Ср.оцен"
						></user-stat-row>

						<user-stat-row
							label="Постов:"
							title="Посты, написанные автором"
							:value="user.posts_count"
							:total-rate="user.posts_sum_rating"
							:rate="user.posts_avg_rating"
						></user-stat-row>

						<user-stat-row
							label="Комментов:"
							title="Комментарии, написанные автором"
							:value="user.comments_count"
							:total-rate="user.comments_sum_rating"
							:rate="user.comments_avg_rating"
						></user-stat-row>

						<user-stat-row
							label="Лайков:"
							title="Лайки, поставленные автором"
							:value="user.rate_likes_count"
						></user-stat-row>

						<user-stat-row
							label="Дизлайков:"
							title="Дизлайки, поставленные автором"
							:value="user.rate_dislikes_count"
						></user-stat-row>
					</div>

					<div v-if="currentUser.id() && !isCurrentUserProfile"
						 class="mt-8 space-x-2 justify-between items-center flex">
						<subscribe-button
							v-if="currentUser.id() && !isCurrentUserProfile"
							type="user"
							:id="user.id"
							:subscribed="user.is_subscribed"
							:notify="user.is_subscribed_with_notifications"
						></subscribe-button>

						<block-user
							v-if="currentUser.id() && !isCurrentUserProfile"
							:userid="user.id"
							:block="user.block">
						</block-user>
					</div>

				</div>

				<moder-stat v-if="currentUserData && currentUserData.can_moderate" :user_id="user.id"></moder-stat>

				<div v-if="user.badges.length > 0" class="my-8 p-6 text-center">
					<div class="text-xl font-bold mb-6">
						Ачивки
					</div>
					<div class="flex flex-wrap justify-center items-center">
						<badge class="m-4" v-for="badge in user.badges" :key="badge.id" :badge="badge"></badge>
					</div>
				</div>

				<div class="bottom-tabs whitespace-nowrap">
					<a v-for="item in filteredParts"
					   @click.prevent="setPart(item, true)"
					   :href="user.url + (item.url ? '/' + item.url: '')"
					   :class="{'active': selectedPart === item.value}">
						{{ item.label }}
					</a>
				</div>
			</div>

			<div class="max-w-2xl mx-auto space-y-8">

				<ban-block v-if="user.ban" :ban="user.ban" class=""></ban-block>

				<warn-block v-if="user.warns.length" :warns="user.warns" class=""></warn-block>

				<div v-for="item in filteredParts">
					<div v-if="selectedPart === 'drafts' && selectedPart === item.value && isCurrentUserProfile"
						 class="text-right mb-4"
					>
						<button @click.prevent="deleteDraftsWithConfirm" class="btn btn-danger btn-sm">
							Удалить все черновики
						</button>
					</div>
					<infinity-scroll
						v-if="selectedPart === item.value"
						:page="page"
						:dontscroll="true"
						:with-sort="selectedPart === 'posts' || selectedPart === 'comments'"
						:url="host + '/api/v1.1/user/' + this.userid + '/' + selectedPart"
					></infinity-scroll>
				</div>

			</div>
		</div>
	</div>
</template>
<script>

import Client from "../../js/modules/Client";
import {notify} from "@kyvg/vue3-notification";
import User from "../../js/modules/User";
import plural from "../../js/modules/Plural";
import BanBlock from "../partials/BanBlock.vue";
import WarnBlock from "../partials/WarnBlock.vue";
import BlockUser from "../partials/BlockUser.vue";
import UserStatRow from "./UserStatRow.vue";
import Badge from "../partials/Badge.vue";
import ValentineForm from "./ValentineForm.vue";
import Loader from "../partials/Loader.vue";

export default {
	components: {Loader, ValentineForm, Badge, UserStatRow, BlockUser, WarnBlock, BanBlock},
	props: [
		'userid',
		'part',
		'isValentineDaySendPeriod',
	],
	data() {
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		const currentUser = new User();
		const isAdmin = currentUser && currentUser.data() && currentUser.data().can_admin;

		const host = window.location.protocol + '//' + window.location.host;

		let selectedPart = this.part ?? 'posts';
		let parts = [
			{label: 'Статьи', value: 'posts', url: '', visible: true},
			{label: 'Комментарии', value: 'comments', url: 'comments', visible: true},
			{
				label: 'Черновики',
				value: 'drafts',
				url: 'drafts',
				type: 'posts',
				visible: () => this.isCurrentUserProfile || isAdmin
			},
			{label: 'Подписчики', value: 'subscribers', url: 'subscribers', visible: true},
			{label: 'Подписки', value: 'subscriptions', url: 'subscriptions', visible: () => this.isCurrentUserProfile},
			{label: 'Черный список', value: 'blacklist', url: 'blacklist', visible: () => this.isCurrentUserProfile},
		];

		let initialPart = parts.find(item => item.value === selectedPart);

		return {
			page: urlParams.has('page') ? parseInt(urlParams.get('page')) : null,
			loading: true,
			user: null,
			currentUser: currentUser,
			currentUserData: currentUser ? currentUser.data() : {},
			editMode: false,
			selectedPart: selectedPart,
			host: host,
			dicts: {
				parts: parts,
			},
			initialPart: initialPart,
		}
	},
	mounted() {
		Client
			.get('/api/v1.1/user/' + this.userid)
			.then(response => {
				this.user = response.data.data;
			})
			.catch(() => {
				notify({
					type: 'error',
					text: 'Не удалось загрузить данные пользователя',
				});
			})
			.finally(() => {
				this.loading = false;
			});

		window.addEventListener("popstate", (event) => {
			if (event.state && event.state.item) {
				this.setPart(event.state.item);
			} else {
				this.setPart(this.initialPart);
			}
		});
	},
	methods: {
		setPart(item, setState = false) {
			let itemData = JSON.parse(JSON.stringify(item));
			this.selectedPart = itemData.value;

			if (setState) {
				window.history.pushState({item: itemData}, document.title, this.user.url + (itemData.url ? '/' + itemData.url : ''));
			}
		},
		setPartByValue(value) {
			let parts = this.filteredParts.filter(p => p.value === value);
			if (parts.length === 0) {
				return;
			}
			let item = parts[0];
			this.setPart(item, true);
		},
		plural(cnt) {
			return plural(cnt, ['подписчик', 'подписчика', 'подписчиков']);
		},
		deleteDraftsWithConfirm() {
			if (confirm('Все ваши черновики будут полностью удалены. Действие необратимо. Вы уверены?')) {
				Client.delete('/api/v1.1/user/' + this.userid + '/drafts')
					.then(() => {
						notify({
							text: 'Ваши черновики поставлены в очередь на удаление. Удаление большого количества постов может занять до нескольких часов.',
							type: 'success',
						})
					})
					.catch(() => {
						notify({
							text: 'Не вышло',
							type: 'error',
						})
					})
			}
		}
	},
	computed: {
		filteredParts() {
			return this.dicts.parts.filter(item => {
				if (typeof item.visible === 'function') {
					let func = item.visible;
					return func();
				}

				return item.visible
			});
		},
		isCurrentUserProfile() {
			return this.currentUser && this.currentUser.id() === this.user.id;
		}
	}
}
</script>
